import { ThemeComponent } from '@/theme/ThemeComponent';
import { Component, Prop, Ref } from '@eastsideco/salvo-ts';
import { cart } from '@/theme/Cart';
import type { QuantitySelector } from '../QuantitySelector/QuantitySelector';
import './CartItem.scss';
import type { ShopifyCartItemInput } from '@/theme/cart/types/ICartApi';
import type { ICartItem } from '@/theme/cart/types/ICartItem';

@Component
export class CartItem extends ThemeComponent {
    @Prop() item!: Shopify.Liquid.LineItem;

    @Ref({ required: false }) quantitySelector!: QuantitySelector;
    @Ref({ required: false }) removeItemButton!: HTMLButtonElement[];
    @Ref({ required: false }) purchaseType!: HTMLElement;
    @Ref({ required: false }) upgradeButton!: HTMLButtonElement;
    @Ref({ required: false }) upgradeSelect!: HTMLSelectElement;

    init() {

        // Listen for item remove clicks
        for (const button of this.removeItemButton) {
            this.$listen(button, 'click', async() => {
                await this.handleQuantity(0);
            });
        }

        // Subscribe to quantity change
        if (this.quantitySelector) {
            this.$subscribe(this.quantitySelector.onChange, (quantity) => {
                this.handleQuantity(quantity).catch((e) => {
                    this.$log.error(`Update quantity failed`, { e });
                });
            });
        }

        // Listen for product upgrade clicks
        if (this.upgradeButton) {
            this.$listen(this.upgradeButton, 'click', async() => {
                await this.handleUpgradeBtnClick();
            });
        }

        // Listen for upgrade select changes
        if (this.upgradeSelect) {
            this.$listen(this.upgradeSelect, 'change', async() => {
                await this.handleUpgradeSelectChange();
            });
        }
    }
    

    async handleQuantity(quantity: number) {
        // Attempt quantity update
        const itemsToUpdate:ICartItem[] = [];

        // Find matching cart item
        const matchingItem = cart.items.find((item) => item.id === this.item.id);
        if (!matchingItem) {
            this.$log.error(`No matching item found`);
            return;
        }

        // Add matching item to array of items to update
        itemsToUpdate.push(matchingItem);

        /*
            Check whether there is a free gift with
            _eligibleProductId that matches the item
        */
        // eslint-disable-next-line
        const matchingGiftItems: ICartItem[]|undefined = cart.items.filter((item) => item.properties['_eligibleProductId'] == matchingItem.id);

        if (matchingGiftItems) {
            for (const item of matchingGiftItems) {
                itemsToUpdate.push(item);
            }
        }

        // Update cart item quantities
        try {
            await cart.asInteraction( async() => {
                await cart.updateItemQuantities(
                    cart.items.map((item) => itemsToUpdate.includes(item) ? quantity : item.quantity)
                );
            });
        } catch (event: unknown) {
            const { message } = event as Error;
            this.$log.error('Cart item quantity update error', message);
        }
    }

    async handleUpgradeBtnClick() {
        this.upgradeButton.innerText = 'Upgrading...';
        const upgradeSelect = this.upgradeButton.closest('.cart-item__inner')?.querySelector('select');
        if (!upgradeSelect) {
            return;
        }

        const firstSubscriptionOption = Array.from(upgradeSelect.options).find(option => 
            option.getAttribute('selling-plan-id') !== null
        );

        if (firstSubscriptionOption) {
            await this.handleProductUpgrade(firstSubscriptionOption);
        }
    }

    async handleUpgradeSelectChange() {
        const selectedOption = this.upgradeSelect.options[this.upgradeSelect.selectedIndex];
        await this.handleProductUpgrade(selectedOption);
    }

    async handleProductUpgrade(selectedOption: HTMLOptionElement) {
        // Properties based on the current item
        const currentItem = this.item;
        const currentItemPackSize = (currentItem.properties as Record<string, unknown>)['_pack_size'] 
            ? Number((currentItem.properties as Record<string, unknown>)['_pack_size']) 
            : 1;

        // Properties based on the selected option
        const selectedOptionValue = selectedOption.value;
        const selectedOptionIsOneTime = selectedOption.getAttribute('one-time') === 'true';
        const selectedOptionPackSize = selectedOption.getAttribute('pack-size') || '1';
        const selectedOptionSellingPlanId = selectedOption.getAttribute('selling-plan-id') ? Number(selectedOption.getAttribute('selling-plan-id')) : undefined;
        const selectedOptionFreeGifts = selectedOption.getAttribute('gift-ids');
        const selectedOptionParentProductUrl = selectedOption.getAttribute('parent-product-url');
        const selectedOptionUpsellId = selectedOption.getAttribute('upsell-id');
        const selectedOptionCheckoutImage = selectedOption.getAttribute('checkout-image');
        let properties = {};

        // Pass the correct properties based on the selected option
        if(selectedOptionIsOneTime) {
            properties = {
                '_checkout_image': selectedOptionCheckoutImage,
                '_linked_product_handles': (currentItem.properties as Record<string, unknown>)['_linked_product_handles'],
                '_linked_product_quantities': (currentItem.properties as Record<string, unknown>)['_linked_product_quantities'],
                '_main_product_image': (currentItem.properties as Record<string, unknown>)['_main_product_image'],
                '_one_time_purchase_id': (currentItem.properties as Record<string, unknown>)['_one_time_purchase_id'],
                '_parent_product_url': (currentItem.properties as Record<string, unknown>)['_parent_product_url'],
            }
        } else {
            properties = {
                ...(currentItem.properties || {}),
                '_checkout_image': selectedOptionCheckoutImage,
                '_display_quantity': selectedOptionPackSize,
                '_free_gift_ids': selectedOptionFreeGifts,
                '_flavour': "",
                '_hide_qty': "false",
                '_item_absoluter_status': selectedOption.getAttribute('item-absoluter-status'),
                '_pack_size': selectedOptionPackSize,
                '_parent_product_url': selectedOptionParentProductUrl,
                '_upsell_id': selectedOptionUpsellId
            }
        }
        
        // Find matching cart item
        const matchingItem = cart.items.find((item) => item.key === this.item.key);
        if (!matchingItem) {
            this.$log.error(`No matching item found`);
            return;
        }

        // Calculate the new quantity based on the current item pack size and the selected option pack size
        const newQuantity = matchingItem.quantity / Number(currentItemPackSize) * Number(selectedOptionPackSize);

        // Create the product item to be added to the cart
        const productItem = {
            id: Number(selectedOptionValue),
            selling_plan: selectedOptionSellingPlanId ?? undefined,
            quantity: newQuantity,
            properties: {
                ...(properties ?? {})
            }
        } satisfies ShopifyCartItemInput;

        // Check if free gifts exist
        const selectedOptionFreeGiftsStripped = selectedOptionFreeGifts?.replace(/[{}"]/g, '');
        const freeGifts = selectedOptionFreeGiftsStripped ? selectedOptionFreeGiftsStripped.split(',') : [];
        let freeGiftIds = [];

        // Loop through the free gifts and strip unnecessary characters and remove nulls
        for (const gift of freeGifts) {
            const [, giftId] = gift.split(':');
            if (!giftId.includes('null')) {
                freeGiftIds.push(parseInt(giftId));
            }
        }

        // If free gifts exist then prepare them for the add to cart
        if(freeGiftIds.length > 0) {
            const giftIds: ShopifyCartItemInput[] = [];
            const freeGiftQty = freeGifts[0] === freeGifts[1] ? 2 : 1;

            // Prep the add to cart items for free gifts
            for (const freeGiftId of freeGiftIds) {
                giftIds.push({
                    id: Number(freeGiftId),
                    quantity: freeGiftQty,
                    properties: {
                        _eligibleProductId: String(selectedOptionValue),
                        _free_gift: 'true'
                    }
                });
                if (freeGiftQty === 2 ) { break; } // Exit loop when free gifts are the same.
            }

            // Spread the product item and the gift items into an array
            const productWithGifts = [...giftIds, productItem];

            // Update the cart item quantities and add the product and gift items to the cart
            await cart.asInteraction(async() => {
                await cart.updateItemQuantities(
                    cart.items.map((item) => item == matchingItem ? 0 : item.quantity)
                );

                await cart.addItems(productWithGifts).catch((event: unknown | null) => {
                    if (event instanceof Error) {
                        this.$log.error('Cart item quantity update error', event.message);
                    }
                });
            });
        } else {
            // Update the cart item quantities and remove the eligible item from the cart
            await cart.asInteraction(async() => {
                await cart.updateItemQuantities(
                    cart.items.map((item) => item == matchingItem ? 0 : item.quantity)
                );

                // Find free gifts that match the current item and remove them from cart
                const eligibleItem = cart.items.find(item => item.properties._eligibleProductId === String(matchingItem.variantId));
                
                if(eligibleItem) {
                    await cart.removeItem(eligibleItem);
                }
                
                await cart.addItem(productItem).catch((event: unknown | null) => {
                    if (event instanceof Error) {
                        // this.showError(event.message);
                        window.console.warn('Filtering Error.');
                    }
                });
            });
        }
    }
}
